import { AxiosError, isAxiosError } from 'axios';

import {
  CreateProductPayload,
  ProductAttributesPayload,
  DisplayUnit as ProductDisplayUnit,
  DietaryPreferences as ProductDietaryPreferences,
  MandatoryWarning as ProductMandatoryWarning,
  CreateProductError,
} from 'apis/product-information-catalog/types';
import {
  DietaryPreference,
  Item,
  ItemDisplayUnit,
  MandatoryWarning,
  ProductHierarchyLevel,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

const hasNutritionValue = (nutritionValue: { amount: string; nutrition_value_id: string }) =>
  nutritionValue.amount !== '';

const NUTRITION_VALUES_BASELINE_MAP = {
  per_100g: 'PER_100G' as const,
  per_100ml: 'PER_100ML' as const,
};

const DISPLAY_UNIT_MAP: Record<ItemDisplayUnit, ProductDisplayUnit> = {
  units: 'UNITS',
  volume: 'VOLUME',
  volume_100ml: 'VOLUME_100ML',
  weight: 'WEIGHT',
  weight_100g: 'WEIGHT_100G',
};

const DIETARY_PREFERENCES_MAP: Record<DietaryPreference, ProductDietaryPreferences> = {
  'gluten-free': 'GLUTEN_FREE',
  'lactose-free': 'LACTOSE_FREE',
  vegan: 'VEGAN',
  vegetarian: 'VEGETARIAN',
  kosher: 'KOSHER',
  halal: 'HALAL',
};

const MANDATORY_WARNING_MAP: Record<MandatoryWarning, ProductMandatoryWarning> = {
  'with-alcohol-soft': 'WITH_ALCOHOL_SOFT',
  'with-alcohol-hard': 'WITH_ALCOHOL_HARD',
  'choking-sausages': 'CHOKING_SAUSAGES',
  'choking-nuts': 'CHOKING_NUTS',
  'with-sugar': 'WITH_SUGAR',
  'with-fat': 'WITH_FAT',
  'with-sodium': 'WITH_SODIUM',
  'medicine-otc': 'MEDICINE_OTC',
};

const OTHER_OTHER_OTHER_PRODUCT_FAMILY = '999999999';

export const transformItemToProductAttributes = (item: Item): ProductAttributesPayload => ({
  additives: item.additives,
  alcoholPermille: Number(item.alcohol_percentage) ?? null,
  allergens: item.allergens,
  brandId: item.brand_id,
  conditionsOfUseAndStorage: item.conditions_of_use_and_storage,
  countryOfOrigin: item.country_of_origin,
  description: item.description,
  dietaryPreferences: item.dietary_preferences.map(dp => DIETARY_PREFERENCES_MAP[dp]),
  displayUnit: item.display_unit ? DISPLAY_UNIT_MAP[item.display_unit] : null,
  distributorInformation: item.distributor_information,
  ingredients: item.ingredients,
  mandatoryWarnings: item.mandatory_warnings.map(mw => MANDATORY_WARNING_MAP[mw]),
  moreInformation: item.more_information,
  name: item.name,
  numberOfUnits: item.number_of_units ?? null,
  nutritionDetails: item.nutrition_values_baseline
    ? {
        baseline: NUTRITION_VALUES_BASELINE_MAP[item.nutrition_values_baseline],
        values: item.nutrition_values
          .filter(hasNutritionValue)
          .map(nv => ({ id: nv.nutrition_value_id, value: nv.amount })),
      }
    : null,
  nutritionFacts: item.nutrition_facts,
  producerInformation: item.producer_information,
  volumeInMl: item.volume_in_ml ?? null,
  weightInGrams: item.sell_by_weight_config?.grams_per_step ?? item.weight_in_grams ?? null,
});

export const transformItemToProductData = (
  item: Item,
  productHierarchyLevels: ProductHierarchyLevel[],
  mediaIds: string[],
): CreateProductPayload['items'][number] => {
  return {
    productFamilyId:
      productHierarchyLevels.find(level => level.id === item.product_hierarchy_level_id)?.human_readable_id ??
      OTHER_OTHER_OTHER_PRODUCT_FAMILY,
    gtin: item.barcode_gtin ?? '',
    media: mediaIds,
    attributes: transformItemToProductAttributes(item),
  };
};

export const getQueryKeys = (venue: Venue) => ({
  searchProductsToAdd: ['/v2/products/search', 'bulk-add-items', venue.country, venue.menu],
  searchProductsToConnect: ['/v2/products/search', 'bulk-connect-items', venue.country, venue.menu],
});

export const isCreateProductError = (error: unknown): error is AxiosError<CreateProductError> =>
  isAxiosError(error) && typeof error.response?.data?.details === 'string';
